import { useState, useEffect, useCallback, useRef } from 'react';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Modal from 'react-bootstrap/Modal';
import InputTextField from '../../form/InputTextField';
import DropDownField from '../../form/DropDownField';
import { useForm } from 'react-hook-form';
import HTTP from '../../../config/axios';
import { emailPattern, fieldRequired, passwordPattern, phoneNumberPattern } from '../../../config/validations/rules';
import SimpleToast from '../../common/Toast';
import { encryptLocalStorage } from '../../../config/utils/storage';
import HierarchicalOrgSelector from './HierarchicalOrgSelector';

interface responseType {
    success: boolean;
    error: any;
    data: {
        err?: string;
        status: number,
        error: string,
        msg: string,
        message: string,
        type: string
    };
}
interface IClientInfo {
    id: number;
    firstName: string;
    lastName: string;
    phone: number;
    email: string;
    orgId : number;
    roleId?: number;
    roleName?: string;
}

interface AddEditModalProps {
    show: boolean;
    handleClose: () => void;
    clientDetails: IClientInfo;
}

const UserAddEditModal: React.FC<AddEditModalProps> = ({ show, handleClose, clientDetails }) => {

    const [disableEmail, setDisableEmail] = useState(false)
    const [dealerships, setDealership] = useState<number[]>([]);
    const dealershipsRef = useRef<number[]>([]);
    const [roles, setRoles] = useState([])
    const [allDealerships, setAllDealerships] = useState<number[]>([]);

    const { handleSubmit, formState: { errors }, setError, clearErrors, control, reset, watch } = useForm<Record<string, any>>();
    const selectedRoleId = watch('roleId');

    useEffect(() => {
        if (clientDetails.id !== 0) {
            setDisableEmail(true)
        }
    }, [clientDetails]);

    useEffect(() => {
        fetchRoles()
        fetchAllDealerships()
        if(!clientDetails.roleId){
            clientDetails.roleId = 0
            clientDetails.roleName = ''
        }
        reset(clientDetails);
    }, [clientDetails]);

    // This useEffect is now handled in the role change logic above



    // Handle role changes
    useEffect(() => {
        const roleId = parseInt(selectedRoleId);
        
        // Skip if no role selected yet
        if (!roleId) return;
        
        if (roleId === 6 || roleId === 7 || roleId === 10) { // HO_ADMIN or MASTER_ADMIN
            setDealership([1]);
            dealershipsRef.current = [1];
        } else {
            // For non-management roles, reset selection to allow user choice
            // But preserve existing user's orgId on initial load
            if (clientDetails.id !== 0 && selectedRoleId == clientDetails.roleId && clientDetails.orgId) {
                setDealership([clientDetails.orgId]);
                dealershipsRef.current = [clientDetails.orgId];
            } else {
                setDealership([]);
                dealershipsRef.current = []; // Ensure ref is also cleared
            }
        }
    }, [selectedRoleId, clientDetails.roleId, clientDetails.id, clientDetails.orgId]);

    const fetchRoles = async () => {
        try {
            const userInfo = encryptLocalStorage.getItem("userInfo");
            const roleWeight = userInfo?.roleWeight || 0;
            
            // Debug logging
            console.log('Current user roleWeight:', roleWeight);
            console.log('Full userInfo:', userInfo);
            console.log('Current user roleUUID:', userInfo?.roleUUID);

            const response = await HTTP.get(`/api/admin/user/roles?roleWeight=${roleWeight}`);
            const { data: { status, data } } = response
            
            // Debug logging
            console.log('Roles API response:', { status, data });
            
            if (status == 1) {
               const filteredRoles =  data.filter((rid:any) => (rid.id != 5 && rid.id != 3))
               console.log('Filtered roles:', filteredRoles);
               console.log('Available role UUIDs:', filteredRoles.map((r:any) => ({ id: r.id, name: r.name, uuid: r.uuid })));
                setRoles(filteredRoles)
            }
        }
        catch (error: any) {
            console.error('Error fetching Roles:', error);
            if (error.response?.status === 401) {
                setToast({ open: true, header: 'Access Denied', message: 'You do not have permission to access this resource', variant: 'danger' })
            } else {
                console.error('Error fetching Roles:', error);
            }
            setRoles([]);
        }
    }

    const fetchAllDealerships = async () => {
        try {
            const response = await HTTP.get('/api/admin/user/organizations');
            const { data: { status, data } } = response;
            if (status === 1) {
                const dealershipIds = data.dealerships.map((d: any) => d.id);
                setAllDealerships(dealershipIds);
            }
        } catch (error: any) {
            if (error.response?.status === 401) {
                setToast({ open: true, header: 'Access Denied', message: 'You do not have permission to access this resource', variant: 'danger' })
            } else {
                console.error('Error fetching dealerships:', error);
            }
        }
    };




    const [toast, setToast] = useState({ open: false, header: '', message: '', variant: '' })

    // Memoize the callback to prevent infinite loops
    const handleSelectionChange = useCallback((dealerships: number[]) => {
        setDealership(dealerships);
        dealershipsRef.current = dealerships;
    }, []);

    const onSubmit = async (data: any) => {
      try {
        // Use ref to get the most current dealership selection
        const currentDealerships = dealershipsRef.current;
        data['dealership'] = currentDealerships;
        
        // Validation for non-management roles
        if (parseInt(selectedRoleId) !== 6 && parseInt(selectedRoleId) !== 7 && parseInt(selectedRoleId) !== 10 && currentDealerships.length === 0) {
            setToast({ open: true, header: 'Validation Error', message: 'Please select at least one organization', variant: 'danger' })
            return;
        }
        
        if (data.id !== 0) {
            // UPDATE USER
            // Prevent HO Admin users from being updated if role remains the same
            // if ((parseInt(selectedRoleId) === 6 || parseInt(selectedRoleId) === 7) && 
            //     selectedRoleId == clientDetails.roleId) {
            //     setToast({ open: true, header: 'Validation Error', message: 'Cannot update HO Admin user without changing role', variant: 'danger' })
            //     return;
            // }
            
            const userData: responseType = await HTTP.post(`/api/admin/user/edit`, data);
            if (userData.data.status === 1) {
                setToast({ open: true, header: 'Success', message: 'User info updated successfully', variant: 'success' })
            } else {
                setToast({ open: true, header: 'failed', message: userData.data.message || 'Something went wrong', variant: 'danger' })
            }
        } else {
            // CREATE USER
            delete data.id;
            const userData: responseType = await HTTP.post(`/api/admin/user/create`, data);
            if (userData.data.status === 1) {
                setToast({ open: true, header: 'Success', message: 'User created successfully', variant: 'success' })
            } else {
                setToast({ open: true, header: 'failed', message: userData.data.message || 'Something went wrong', variant: 'danger' })
            }
        }
        
        handleClose()
        reset()
        } catch(err: any){
            if (err.response?.status === 401) {
                setToast({ open: true, header: 'Access Denied', message: 'You do not have permission to perform this action', variant: 'danger' })
            } else {
                setToast({ open: true, header: 'Failed', message: 'Something went wrong', variant: 'danger' })
            }
        }
    }

    const closeModal = () => {
        handleClose()
        reset()
    }

    return (
        <>
            <Modal show={show} onHide={() => closeModal()}>
                <Form onSubmit={handleSubmit(onSubmit)}>
                    <Modal.Header closeButton>
                        <Modal.Title>{clientDetails.id === 0 ? 'Add User' : 'Update User'}</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className='mb-1'>
                            <InputTextField name="firstName" label="First name"
                                placeholder="Enter first name" clearErrors={clearErrors}
                                control={control} rule={{ required: fieldRequired }} />
                        </div>
                        <div className='mb-1'>
                            <InputTextField name="lastName" label="Last name"
                                placeholder="Enter last name" clearErrors={clearErrors} rule={{ required: fieldRequired }}
                                control={control} />
                        </div>
                        <InputTextField name="id" label=""
                            placeholder="" type="hidden" clearErrors={clearErrors}
                            control={control} rule={{ required: fieldRequired }} />
                        <div className='mb-1'>
                            <InputTextField name="phone" label="Phone number"
                                placeholder="Enter phone number" clearErrors={clearErrors}
                                control={control} rule={{ required: fieldRequired, pattern: phoneNumberPattern }} />
                        </div>
                        <div className='mb-1'>
                            <InputTextField name="email" label="Email"
                                placeholder="Enter email" clearErrors={clearErrors}
                                control={control}
                                rule={{ required: fieldRequired, pattern: emailPattern }}
                            />
                        </div>
                            <div className='mb-1'>
                                <InputTextField name="password" label={clientDetails.id === 0 ? "Password" : "Reset Password"}
                                    type="password" placeholder={clientDetails.id === 0 ? "Enter password" : "Enter new password"}
                                    clearErrors={clearErrors} control={control}
                                rule={clientDetails.id === 0 ? { required: fieldRequired, pattern: passwordPattern } : { pattern: passwordPattern }} />
                            </div>
                        <div className='mb-1'>
                            <DropDownField label="Role" name="roleId" options={roles} control={control} rule={{ required: fieldRequired }} clearErrors={clearErrors} placeholder="Choose role" />
                        </div>
                        <div className="mb-3">
                            <HierarchicalOrgSelector 
                                key={`${clientDetails.id}-${clientDetails.orgId}`}
                                onSelectionChange={handleSelectionChange}
                                selectedDealership={parseInt(selectedRoleId) === 6 || parseInt(selectedRoleId) === 7 || parseInt(selectedRoleId) === 10 ? [1] : dealerships}
                                disabled={parseInt(selectedRoleId) === 6 || parseInt(selectedRoleId) === 7 || parseInt(selectedRoleId) === 10}
                                autoSelectAll={parseInt(selectedRoleId) === 6 || parseInt(selectedRoleId) === 7 || parseInt(selectedRoleId) === 10}
                            />
                        </div>
                    </Modal.Body>
                    <Modal.Footer>
                        <Button variant="secondary" onClick={() => closeModal()}>
                            Close
                        </Button>
                        {clientDetails.id !== 0 &&
                            <Button variant="primary" type="submit">
                                Update
                            </Button>}
                        {clientDetails.id == 0 &&
                            <Button variant="primary" type="submit">
                                Save Changes
                            </Button>}
                    </Modal.Footer>
                </Form>
            </Modal>
            <SimpleToast show={toast.open} header={toast.header}
                message={toast.message} variant={toast.variant}
                onClose={() => setToast({ ...toast, open: false })} />
        </>
    );
}

export default UserAddEditModal;